'use client';

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSession } from './SessionProvider';
import { Brain, AlertTriangle, CheckCircle, Info, Activity, Zap, Wifi, WifiOff, Loader2 } from 'lucide-react';

// ─── EEG Channel Signal Generator ────────────────────────────────────────────
function generateNamedChannelBatch(sampleRate = 256, windowSeconds = 2) {
  const numSamples = sampleRate * windowSeconds;
  const channelNames = ['Fp1', 'Fp2', 'F3', 'F4', 'C3', 'C4', 'P3', 'P4'];
  const channels: Record<string, number[]> = {};
  channelNames.forEach((ch) => {
    channels[ch] = Array.from({ length: numSamples }, (_, i) => {
      const t = i / sampleRate;
      return (
        Math.sin(2 * Math.PI * 10 * t) * 15 +
        Math.sin(2 * Math.PI * 6 * t) * 10 +
        Math.sin(2 * Math.PI * 20 * t) * 5 +
        (Math.random() - 0.5) * 8
      );
    });
  });
  return { sample_rate: sampleRate, window_seconds: windowSeconds, channels, timestamp: new Date().toISOString() };
}

// ─── Backend API Response Interface ──────────────────────────────────────────
interface ADHDAPIResponse {
  severity?: string;
  severity_score?: number;
  risk_score?: number;
  risk_level?: string;
  theta_beta_ratio?: number;
  theta_power?: number;
  beta_power?: number;
  attention_score?: number;
  confidence?: number;
  message?: string;
  timestamp?: string;
}

// ─── ADHD Risk Classification ─────────────────────────────────────────────────
interface RiskLevel {
  label: string;
  color: string;
  bg: string;
  border: string;
  glow: string;
  icon: React.ReactNode;
  description: string;
  recommendation: string;
  score: number;
}

function classifyRiskFromScore(score: number, severity?: string): RiskLevel {
  const s = (severity ?? '').toLowerCase();

  // Prefer severity string if provided
  if (s.includes('low') || s.includes('normal') || s.includes('none') || score < 30) {
    return {
      label: 'Stable Pattern', color: '#22d3ee',
      bg: 'rgba(34,211,238,0.08)', border: 'rgba(34,211,238,0.3)', glow: 'rgba(34,211,238,0.2)',
      icon: <CheckCircle className="w-5 h-5" style={{ color: '#22d3ee' }} />,
      description: 'Attention regulation patterns appear within expected range.',
      recommendation: 'Maintain current cognitive engagement. Continue focused training sessions.',
      score: Math.min(score, 29),
    };
  }
  if (s.includes('borderline') || s.includes('mild') || score < 55) {
    return {
      label: severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'Mild Variation',
      color: '#F97316', bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.3)', glow: 'rgba(249,115,22,0.2)',
      icon: <Info className="w-5 h-5" style={{ color: '#F97316' }} />,
      description: 'Slightly elevated theta relative to beta. May indicate mild attention drift or fatigue.',
      recommendation: 'Consider a short break or mindfulness exercise to support beta-band activity.',
      score: Math.min(score, 54),
    };
  }
  if (s.includes('elevated') || s.includes('moderate') || score < 75) {
    return {
      label: severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'Attention Variability',
      color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.3)', glow: 'rgba(245,158,11,0.2)',
      icon: <AlertTriangle className="w-5 h-5" style={{ color: '#f59e0b' }} />,
      description: 'Elevated theta/beta ratio — a pattern associated with attention variability indicators.',
      recommendation: 'Engage in focused breathing or neurofeedback. Reduce distractions.',
      score: Math.min(score, 74),
    };
  }
  return {
    label: severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : 'High Variability',
    color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.3)', glow: 'rgba(239,68,68,0.25)',
    icon: <AlertTriangle className="w-5 h-5" style={{ color: '#ef4444' }} />,
    description: 'High theta/beta ratio — a pattern associated with significant attention variability in research literature.',
    recommendation: 'Consider consulting a specialist. Neurofeedback training may be beneficial.',
    score: Math.min(100, score),
  };
}

// Fallback: classify from raw TBR
function classifyRiskFromTBR(tbr: number): RiskLevel {
  if (tbr < 1.2) return classifyRiskFromScore(Math.round((tbr / 1.2) * 29), 'low');
  if (tbr < 1.6) return classifyRiskFromScore(Math.round(30 + ((tbr - 1.2) / 0.4) * 24), 'borderline');
  if (tbr < 2.0) return classifyRiskFromScore(Math.round(55 + ((tbr - 1.6) / 0.4) * 19), 'elevated');
  return classifyRiskFromScore(Math.min(100, Math.round(75 + ((tbr - 2.0) / 0.8) * 25)), 'high');
}

// ─── Gauge Arc ────────────────────────────────────────────────────────────────
function RiskGauge({ score, color, glow }: { score: number; color: string; glow: string }) {
  const radius = 52;
  const strokeWidth = 8;
  const cx = 70;
  const cy = 70;
  const startAngle = -210;
  const totalAngle = 240;
  const angle = startAngle + (score / 100) * totalAngle;

  function polarToXY(angleDeg: number, r: number) {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(startDeg: number, endDeg: number, r: number) {
    const start = polarToXY(startDeg, r);
    const end = polarToXY(endDeg, r);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  }

  const trackPath = arcPath(startAngle, startAngle + totalAngle, radius);
  const fillPath = score > 0 ? arcPath(startAngle, angle, radius) : '';
  const needleEnd = polarToXY(angle, radius - 4);

  const zones = [
    { from: startAngle, to: startAngle + totalAngle * 0.3, color: '#22d3ee' },
    { from: startAngle + totalAngle * 0.3, to: startAngle + totalAngle * 0.6, color: '#F97316' },
    { from: startAngle + totalAngle * 0.6, to: startAngle + totalAngle * 0.85, color: '#f59e0b' },
    { from: startAngle + totalAngle * 0.85, to: startAngle + totalAngle, color: '#ef4444' },
  ];

  return (
    <div className="flex items-center justify-center">
      <svg width="140" height="100" viewBox="0 0 140 100">
        <path d={trackPath} fill="none" stroke="rgba(30,58,95,0.4)" strokeWidth={strokeWidth} strokeLinecap="round" />
        {zones.map((zone, i) => (
          <path key={i} d={arcPath(zone.from, zone.to, radius)} fill="none" stroke={zone.color}
            strokeWidth={strokeWidth} strokeLinecap="butt" opacity={0.18} />
        ))}
        {fillPath && (
          <path d={fillPath} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 6px ${glow})`, transition: 'all 0.8s cubic-bezier(0.34,1.56,0.64,1)' }} />
        )}
        <circle cx={needleEnd.x} cy={needleEnd.y} r={5} fill={color}
          style={{ filter: `drop-shadow(0 0 8px ${color})`, transition: 'all 0.8s cubic-bezier(0.34,1.56,0.64,1)' }} />
        <text x={cx} y={cy - 4} textAnchor="middle" fill="white" fontSize="18" fontWeight="900" fontFamily="monospace">{score}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fill="#4A7AB5" fontSize="8" fontWeight="600" letterSpacing="1">RISK SCORE</text>
        <text x={14} y={96} textAnchor="middle" fill="#22d3ee" fontSize="7" fontWeight="700">LOW</text>
        <text x={126} y={96} textAnchor="middle" fill="#ef4444" fontSize="7" fontWeight="700">HIGH</text>
      </svg>
    </div>
  );
}

// ─── Metric Pill ──────────────────────────────────────────────────────────────
function MetricPill({ label, value, unit, color }: { label: string; value: string; unit: string; color: string }) {
  return (
    <div className="flex flex-col items-center px-3 py-2 rounded-xl"
      style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
      <span className="text-[18px] font-black leading-none" style={{ color }}>{value}</span>
      <span className="text-[9px] text-[#4A7AB5] mt-0.5 font-semibold">{unit}</span>
      <span className="text-[8px] text-[#4A7AB5] mt-0.5 uppercase tracking-widest">{label}</span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ADHDRiskPanel() {
  const { metrics } = useSession();
  const { thetaPower, betaPower, thetaAlphaRatio, attentionScore } = metrics;

  const apiUrl = process.env.NEXT_PUBLIC_EEG_API_URL;

  // Backend state
  const [apiResult, setApiResult] = useState<ADHDAPIResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isApiOnline, setIsApiOnline] = useState<boolean | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMounted = useRef(true);

  // ── POST to /adhd-detection ──────────────────────────────────────────────
  const fetchADHDDetection = useCallback(async () => {
    if (!apiUrl) return;
    setIsLoading(true);
    try {
      const payload = generateNamedChannelBatch(256, 2);
      const res = await fetch(`${apiUrl}/adhd-detection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(8000),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ADHDAPIResponse = await res.json();
      if (isMounted.current) {
        setApiResult(data);
        setIsApiOnline(true);
      }
    } catch {
      if (isMounted.current) setIsApiOnline(false);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    isMounted.current = true;
    fetchADHDDetection();
    intervalRef.current = setInterval(fetchADHDDetection, 4000);
    return () => {
      isMounted.current = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchADHDDetection]);

  // ── Derive display values ────────────────────────────────────────────────
  const { displayTBR, displayTheta, displayBeta, displayAttention, risk, usingRealApi } = useMemo(() => {
    if (apiResult && isApiOnline) {
      const score = apiResult.severity_score ?? apiResult.risk_score ?? null;
      const severity = apiResult.severity ?? apiResult.risk_level ?? '';
      const tbr = apiResult.theta_beta_ratio ?? (betaPower > 0 ? thetaPower / betaPower : thetaAlphaRatio);
      const theta = apiResult.theta_power ?? thetaPower;
      const beta = apiResult.beta_power ?? betaPower;
      const attn = apiResult.attention_score ?? attentionScore;

      const derivedRisk = score !== null
        ? classifyRiskFromScore(Math.round(score), severity)
        : classifyRiskFromTBR(tbr);

      return {
        displayTBR: parseFloat(tbr.toFixed(2)),
        displayTheta: theta,
        displayBeta: beta,
        displayAttention: attn,
        risk: derivedRisk,
        usingRealApi: true,
      };
    }

    // Fallback: local computation
    const localTBR = betaPower > 0 ? parseFloat((thetaPower / betaPower).toFixed(2)) : thetaAlphaRatio;
    return {
      displayTBR: localTBR,
      displayTheta: thetaPower,
      displayBeta: betaPower,
      displayAttention: attentionScore,
      risk: classifyRiskFromTBR(localTBR),
      usingRealApi: false,
    };
  }, [apiResult, isApiOnline, thetaPower, betaPower, thetaAlphaRatio, attentionScore]);

  // Sparkline (last 20 TBR values)
  const tbrHistory = useMemo(() => {
    const base = displayTBR;
    return Array.from({ length: 20 }, (_, i) => {
      const noise = Math.sin(i * 0.7 + base) * 0.15;
      return Math.max(0.4, Math.min(2.8, base + noise));
    });
  }, [displayTBR]);

  const sparkMin = Math.min(...tbrHistory);
  const sparkMax = Math.max(...tbrHistory) + 0.01;
  const sparkH = 28;
  const sparkW = 120;
  const sparkPoints = tbrHistory.map((v, i) => {
    const x = (i / (tbrHistory.length - 1)) * sparkW;
    const y = sparkH - ((v - sparkMin) / (sparkMax - sparkMin)) * sparkH;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, rgba(11,22,40,0.98) 0%, rgba(6,13,26,1) 100%)',
        border: `1px solid ${risk.border}`,
        boxShadow: `0 0 40px ${risk.glow}, 0 8px 32px rgba(0,0,0,0.5)`,
      }}
    >
      {/* Top accent bar */}
      <div className="h-[3px]"
        style={{ background: `linear-gradient(90deg, transparent, ${risk.color} 30%, ${risk.color} 70%, transparent)` }} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: risk.bg, border: `1px solid ${risk.border}` }}>
              <Brain className="w-4.5 h-4.5" style={{ color: risk.color }} />
            </div>
            <div>
              <h3 className="text-[14px] font-black text-white leading-tight">ADHD Risk Indicator</h3>
              <p className="text-[10px] text-[#4A7AB5]">Theta/Beta Ratio Analysis</p>
            </div>
          </div>

          {/* Live / API status badge */}
          <div className="flex items-center gap-1.5">
            {/* API source indicator */}
            <div
              className="flex items-center gap-1 px-2 py-0.5 rounded-full"
              style={{
                background: usingRealApi ? 'rgba(34,211,238,0.1)' : 'rgba(74,122,181,0.1)',
                border: `1px solid ${usingRealApi ? 'rgba(34,211,238,0.3)' : 'rgba(74,122,181,0.25)'}`,
              }}
            >
              {isLoading ? (
                <Loader2 className="w-2.5 h-2.5 animate-spin" style={{ color: '#4A7AB5' }} />
              ) : usingRealApi ? (
                <Wifi className="w-2.5 h-2.5" style={{ color: '#22d3ee' }} />
              ) : (
                <WifiOff className="w-2.5 h-2.5" style={{ color: '#4A7AB5' }} />
              )}
              <span className="text-[8px] font-bold uppercase tracking-widest"
                style={{ color: usingRealApi ? '#22d3ee' : '#4A7AB5' }}>
                {usingRealApi ? '● REAL API' : '○ COMPUTED'}
              </span>
            </div>

            {/* Live pulse */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
              style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] animate-pulse" />
              <span className="text-[9px] font-bold text-[#F97316] uppercase tracking-widest">Live</span>
            </div>
          </div>
        </div>

        {/* Risk status badge */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-4"
          style={{ background: risk.bg, border: `1px solid ${risk.border}` }}>
          {risk.icon}
          <span className="text-[13px] font-black" style={{ color: risk.color }}>{risk.label}</span>
          <span className="text-[10px] text-[#8BAFD4] ml-auto">TBR: {displayTBR.toFixed(2)}</span>
        </div>

        {/* Gauge */}
        <RiskGauge score={risk.score} color={risk.color} glow={risk.glow} />

        {/* Key metrics row */}
        <div className="grid grid-cols-3 gap-2 mt-4 mb-4">
          <MetricPill label="Theta" value={displayTheta.toFixed(1)} unit="µV²" color="#a855f7" />
          <MetricPill label="Beta" value={displayBeta.toFixed(1)} unit="µV²" color="#22d3ee" />
          <MetricPill label="Attention" value={displayAttention.toFixed(0)} unit="/100" color="#F97316" />
        </div>

        {/* Confidence (only when real API) */}
        {usingRealApi && apiResult?.confidence !== undefined && (
          <div className="flex items-center justify-between mb-3 px-3 py-1.5 rounded-lg"
            style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.15)' }}>
            <span className="text-[9px] text-[#4A7AB5] uppercase tracking-widest font-bold">Model Confidence</span>
            <span className="text-[11px] font-black" style={{ color: '#22d3ee' }}>
              {(apiResult.confidence * (apiResult.confidence <= 1 ? 100 : 1)).toFixed(1)}%
            </span>
          </div>
        )}

        {/* TBR Sparkline */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] text-[#4A7AB5] uppercase tracking-widest font-bold flex items-center gap-1">
              <Activity className="w-2.5 h-2.5" /> TBR Trend
            </span>
            <span className="text-[9px] font-bold" style={{ color: risk.color }}>
              {displayTBR < 1.2 ? 'Normal' : displayTBR < 1.6 ? 'Slightly Elevated' : displayTBR < 2.0 ? 'Elevated' : 'High'}
            </span>
          </div>
          <div className="rounded-lg px-2 py-1.5"
            style={{ background: 'rgba(6,13,26,0.8)', border: '1px solid rgba(30,58,95,0.3)' }}>
            <svg width="100%" height={sparkH} viewBox={`0 0 ${sparkW} ${sparkH}`} preserveAspectRatio="none">
              <polyline points={sparkPoints} fill="none" stroke={risk.color} strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ filter: `drop-shadow(0 0 3px ${risk.color})` }} />
              <line x1="0" y1={sparkH - ((1.2 - sparkMin) / (sparkMax - sparkMin)) * sparkH}
                x2={sparkW} y2={sparkH - ((1.2 - sparkMin) / (sparkMax - sparkMin)) * sparkH}
                stroke="rgba(34,211,238,0.3)" strokeWidth="0.8" strokeDasharray="3 2" />
            </svg>
          </div>
        </div>

        {/* Clinical context */}
        <div className="rounded-xl p-3 mb-3"
          style={{ background: 'rgba(6,13,26,0.6)', border: '1px solid rgba(30,58,95,0.3)' }}>
          <p className="text-[10px] text-[#8BAFD4] leading-relaxed">{risk.description}</p>
        </div>

        {/* Recommendation */}
        <div className="rounded-xl p-3 flex items-start gap-2"
          style={{ background: risk.bg, border: `1px solid ${risk.border}` }}>
          <Zap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: risk.color }} />
          <p className="text-[10px] leading-relaxed" style={{ color: risk.color }}>{risk.recommendation}</p>
        </div>

        {/* Disclaimer */}
        <p className="text-[8px] text-[#4A7AB5]/60 mt-3 text-center leading-relaxed">
          For informational purposes only. Not a clinical diagnosis. Consult a qualified neurologist for medical assessment.
        </p>
      </div>
    </div>
  );
}
